import {
    AlignedRow,
    Cash_movements,
    CellValue,
    DashboardData,
    ObjDataData,
    Payment,
    PriceChange,
    ReducePrinter,
    Today,
    TodayPayment,
    Values,
    Vat as DashboardVat
} from "../../models/dashboard.model";

import { Channels, Vat } from "tilby-models";
import { deduplicateArray, MathUtils } from "src/app/shared/utils";

/*
 * Names of the groups of the fiscal grid tree. They're also used as translation keys (DASHBOARD.DIALOG.<NAME>),
 * except for the names that come from user data (departments, channels, tickets).
 */
export const TICKET = 'ticket';
export const DEPARTMENTS = 'departments';
export const CHANNELS = 'channels';
export const CASH_MOVEMENTS = 'cash_movements';
export const CLAIMED_PAYMENTS_AMOUNT = 'claimed_payments_amount';
export const UNCLAIMED_PAYMENTS_AMOUNT = 'unclaimed_payments_amount';
export const TOTAL_CATEGORIES = ['fiscal_receipts', 'invoices', 'summary_rc_invoices', 'summary_nrc_invoices', 'credit_notes'];

/** Groups whose aggregate is shown, but must not be added to the total of their parent group. */
export const EXCLUDED_FROM_SUM_GROUPS: readonly string[] = ['summary_rc_invoices'];

/** Labels of the vat "exemptions": they use some reserved vat ids. */
export const exempts: Record<string, string> = {
    '0': "ES N4",
    '10': "EE N1",
    '11': "NS N2",
    '12': "NI N3",
    '13': "RM N5",
    '14': "AL N6",
    '15': "VI"
};
export const getVatName = (vatName: string) => exempts[vatName] || `${vatName}%`;

/** Label of a vat: the exemption code, or the vat rate when `vatName` is the id of one of `vats`. */
export const getVatValue = (vats: Vat[], vatName: string | number) => {
    const vatId = Number(vatName);
    const vat = vats?.find(v => v.id === vatId);

    return exempts[vatId] ?? (vat ? `${vat.value}%` : `${vatName}%`);
};

export const sumAmounts = (vats: DashboardVat[] = []) => vats.reduce((sum, v) => sum + (v.amount || 0), 0);

const sumPriceChanges = (printer: ReducePrinter, types: PriceChange['type'][]) =>
    (printer.price_changes ?? []).reduce((sum, change) => types.includes(change.type) ? sum + (change.amount ?? 0) : sum, 0);
export const sumDiscounts = (printer: ReducePrinter) => sumPriceChanges(printer, ['discount_fix', 'discount_perc']);
export const sumSurcharges = (printer: ReducePrinter) => sumPriceChanges(printer, ['surcharge_fix', 'surcharge_perc']);

/**
 * Values that are not split by printer (waste, credit notes, ...) would be repeated in every column,
 * so they are shown only in the column of the "total" printer (or when there's no other column to compare with).
 * Elsewhere `defaultValue` is returned, which the grid shows as empty.
 */
export const justOnce = (n_of_printers: number, isTotalPrinter: boolean, value: number, defaultValue: '' = ''): number | '' =>
    (n_of_printers <= 2 || isTotalPrinter) ? value : defaultValue;

// ---- fiscal grid rows ----

/** A row of the fiscal grid: its path in the tree, and how to get its value for a printer. */
interface Row {
    path: string[];
    value: (printer: ReducePrinter) => CellValue;
}

/** One row per name under `group`, or a single row for the group itself when there are no names. */
const rowsByName = (group: string[], names: (string | number)[], value: (printer: ReducePrinter, name: string | number) => CellValue): Row[] =>
    names.length
        ? names.map(name => ({ path: [...group, String(name)], value: printer => value(printer, name) }))
        : [{ path: group, value: printer => value(printer, group[group.length - 1]) }];

/** The defined values, without duplicates. */
const unique = <T>(items: (T | null | undefined)[]) => deduplicateArray(items.filter((item): item is T => item != null));

const sumPayments = (printer: ReducePrinter, isIncluded: (payment: Payment) => boolean) =>
    (printer.payments ?? []).filter(isIncluded).reduce((sum, payment) => sum + (payment.amount || 0), 0);

type VatGroupName = 'fiscal_receipts' | 'invoices' | 'summary_rc_invoices';

/**
 * Builds the rows of the fiscal grid (ag-grid tree data).
 * Each row has an `orgHierarchy`, its path in the tree, and one more property per printer
 * (the printer name, lowercase and without dots) holding the value of that row for that printer.
 *
 * The first printer is the "total" one, that has all the names (vats, payments, departments, ...) used by the others:
 * the rows are made from it, and every printer then just looks up its own value.
 */
export const alignDataPrinters = (objDataData: ObjDataData, deliveryChannels: Channels[]): AlignedRow[] => {
    const printers = objDataData.printers ?? [];
    const total: Partial<ReducePrinter> = printers[0] ?? {};
    const { today } = objDataData;
    // the total is the first printer (its name is translated, so it cannot be compared with a fixed text)
    const justOncePerPrinter = (printer: ReducePrinter, value: number) => justOnce(printers.length, printer === printers[0], value);

    /** Rows of a "total" group split by vat. The unclaimed amount (when there's one) is shown, but not summed. */
    const vatRows = (group: VatGroupName) => {
        const holder = total[group];

        if (!holder) {
            return [];
        }

        const names = [...holder.vats.map(vat => vat.name), ...(holder.unclaimed !== undefined ? ['unclaimed'] : [])];

        return rowsByName(['total', group], names, (printer, name): Values => {
            if (name === 'unclaimed') {
                return { name, value: printer[group]?.unclaimed || 0, excludeFromSum: true };
            }
            const vat = printer[group]?.vats?.find(v => v.name === name);

            return { name: vat?.name ?? name, value: vat?.amount || 0, parentCount: printer[group]?.count };
        });
    };

    /** Rows for the tickets (the ones in `payments`), or a single row for `group` if there are none. */
    const ticketRows = (group: string[]) => rowsByName(group, unique((total.payments ?? []).map(payment => payment.ticket_name)), (printer, ticket): Values => ({
        name: ticket,
        value: printer.payments?.find(payment => payment.ticket_name === ticket)?.amount || 0,
        count: printer.cash_movements?.tickets?.find(t => t.name === ticket)?.count || 0
    }));

    const claimedNames = unique((total.payments ?? []).filter(payment => !payment.unclaimed && payment.name).map(payment => payment.name));
    const unclaimedPayments = (total.payments ?? []).filter(payment => payment.unclaimed && payment.name);
    // all the unclaimed ticket payments are shown under a single group, named after the first of them
    const unclaimedTicketGroup = unclaimedPayments.find(payment => payment.ticket_name)?.name;
    const unclaimedNames = unique(unclaimedPayments.filter(payment => !payment.ticket_name).map(payment => payment.name));

    const channelName = (channelId: string) => deliveryChannels?.find(channel => channel.id === channelId)?.name;
    const channelsOf = (printer: Partial<ReducePrinter>) => Object.values(printer.channels ?? {});

    const rows: Row[] = [
        ...(['fiscal_receipts', 'invoices', 'summary_rc_invoices'] as const).flatMap(vatRows),
        {
            path: ['total', 'summary_nrc_invoices'],
            value: printer => ({
                name: 'summary_nrc_invoices',
                value: sumAmounts(printer.summary_nrc_invoices?.vats),
                count: printer.summary_nrc_invoices?.count || 0,
                excludeFromSum: true
            })
        },
        {
            path: ['total', 'credit_notes'],
            value: printer => ({
                name: 'credit_notes',
                value: justOncePerPrinter(printer, today.credit_notes?.amount || 0),
                count: justOncePerPrinter(printer, today.credit_notes?.count || 0),
                excludeFromSum: true
            })
        },

        ...(['refunds', 'voids'] as const).map((category): Row => ({
            path: ['refunds_voids', category],
            value: printer => ({ name: category, value: printer[category]?.amount || 0, count: printer[category]?.count || 0 })
        })),

        // "contanti" when there are no payments, so the group is never empty
        ...rowsByName([CLAIMED_PAYMENTS_AMOUNT], claimedNames.length ? claimedNames : ['contanti'], (printer, name): Values =>
            ({ name, value: sumPayments(printer, payment => !payment.unclaimed && payment.name === name) })),

        ...(unclaimedTicketGroup || !unclaimedNames.length ? ticketRows([UNCLAIMED_PAYMENTS_AMOUNT, unclaimedTicketGroup ?? TICKET]) : []),
        ...(unclaimedNames.length ? rowsByName([UNCLAIMED_PAYMENTS_AMOUNT], unclaimedNames, (printer, name): Values =>
            ({ name, value: sumPayments(printer, payment => !!payment.unclaimed && payment.name === name) })) : []),

        ...rowsByName([CASH_MOVEMENTS, 'sales'], ['sales_sum', 'income', 'outcome'], (printer, name): Values => {
            if (name === 'sales_sum') {
                return { name, value: printer.cash_movements?.sales ?? 0, excludeFromSum: true };
            }
            const movement = today.cash_movements?.total?.[name as 'income' | 'outcome'];

            return { name, value: justOncePerPrinter(printer, movement?.amount ?? 0), count: justOncePerPrinter(printer, movement?.count ?? 0) };
        }),
        ...ticketRows([CASH_MOVEMENTS, TICKET]),
        { path: [CASH_MOVEMENTS, 'other_changes_amount'], value: printer => ({ name: 'other_changes_amount', value: printer.other_changes_amount || 0 }) },

        ...rowsByName([DEPARTMENTS], unique((total.departments ?? []).map(department => department.name)), (printer, name): Values =>
            ({ name, value: printer.departments?.find(department => department.name === name)?.amount ?? 0 })),
        ...rowsByName([CHANNELS], unique(channelsOf(total).map(channel => channelName(channel.channel))), (printer, name): Values =>
            ({ name, value: channelsOf(printer).find(channel => channelName(channel.channel) === name)?.amount ?? 0 })),

        { path: ['price_changes'], value: sumDiscounts },
        { path: ['surchanges'], value: sumSurcharges },
        { path: ['wastes'], value: printer => justOncePerPrinter(printer, today.waste || 0) },
        { path: ['food_cost'], value: printer => printer.food_cost || 0 },
    ];

    return rows.map(({ path, value }) => {
        const row: AlignedRow = { orgHierarchy: path };

        printers.forEach((printer, i) => {
            // with two entries (printer + total) the first one is identical to the total, so it's not shown
            if (!printer.name || (printers.length === 2 && i === 0)) {
                return;
            }

            row[printer.name.replace(/\./g, ' ').toLowerCase()] = value(printer);
        });

        return row;
    });
};

const WEATHER_ICONS: Record<number, string> = {
    1: 'weather-clear',
    2: 'weather-clear',
    3: 'weather-few-clouds',
    4: 'weather-few-clouds',
    5: 'weather-drizzle-day',
    6: 'weather-drizzle-day',
    7: 'weather-drizzle-day',
    8: 'weather-clouds',
    9: 'weather-rain-day',
    10: 'weather-showers-day',
    11: 'weather-snow',
    12: 'weather-snow-rain',
    13: 'weather-storm',
    14: 'weather-mist',
    15: 'weather-mist',
    16: 'weather-storm-day',
    17: 'weather-hail',
    18: 'weather-snow-scattered-day'
};

export const getWeatherIcon = (dashboardData: Pick<DashboardData, 'today_weather'>) => {
    const code = Math.trunc(Number(dashboardData.today_weather?.status_code)) || 0;

    return WEATHER_ICONS[code] ?? 'weather-none-available';
};

const PREPAID_PAYMENT_TYPE_ID = 16;

/** The payments of the day, where all the prepaid cards are grouped in a single one. */
export const getPayments = (dashboardData: Pick<DashboardData, 'today_payments'>, labelPrepaidsCards: string): TodayPayment[] => {
    const allPayments = dashboardData.today_payments?.payments ?? [];
    const prepaids = allPayments.filter(payment => payment.type_id === PREPAID_PAYMENT_TYPE_ID);
    const payments = allPayments.filter(payment => payment.type_id !== PREPAID_PAYMENT_TYPE_ID);

    if (prepaids.length > 0) {
        payments.push({
            type_id: PREPAID_PAYMENT_TYPE_ID,
            name: labelPrepaidsCards,
            amount: prepaids.reduce((sum, payment) => sum + (payment.amount || 0), 0)
        });
    }

    return payments;
};

export const getToday = (
    dashboardData: Pick<DashboardData, 'today_dashboard' | 'today_weather'>,
    payments: TodayPayment[],
    current_cash_movements: Cash_movements,
    current_cash_in_register: number,
    weather_icon: string
): Today => {
    const { today_dashboard } = dashboardData;

    return {
        total_amount: MathUtils.round(today_dashboard.items_sold.closed_sales.amount - today_dashboard.refunds.closed_sales.amount),
        total_sales: today_dashboard.closed_sales.total.count,
        payments: payments,
        cash_movements: current_cash_movements,
        current_cash: current_cash_in_register,
        invoices: today_dashboard.invoices,
        fiscal_receipts: today_dashboard.fiscal_receipts,
        summary_invoices: today_dashboard.summary_invoices,
        weather_icon: weather_icon,
        weather: dashboardData.today_weather,
        waste: today_dashboard.waste,
        credit_notes: {
            count: today_dashboard.not_rt_credit_notes?.count ?? 0,
            amount: today_dashboard.not_rt_credit_notes?.amount ?? 0
        }
    };
};
